import { authService } from './authService';

export interface ApiConfig {
  baseURL: string;
  defaultScopes: string[];
}

// API Configuration - update these values for your backend
const API_CONFIG: ApiConfig = {
  baseURL: import.meta.env.VITE_API_BASE_URL || 'https://api.your-backend.com',
  defaultScopes: [
    `api://${import.meta.env.VITE_AZURE_CLIENT_ID}/access_as_user`
    // Add your custom API scopes here
    // Example: 'api://your-api-client-id/access_as_user'
  ],
};

class ApiService {
  private baseURL: string;
  private defaultScopes: string[];

  constructor(config: ApiConfig) {
    this.baseURL = config.baseURL;
    this.defaultScopes = config.defaultScopes;
  }

  /**
   * Make authenticated GET request
   */
  async get<T>(endpoint: string, scopes?: string[]): Promise<T> {
    const response = await this.makeRequest('GET', endpoint, undefined, scopes);
    return this.handleResponse<T>(response);
  }

  /**
   * Make authenticated POST request
   */
  async post<T>(endpoint: string, data?: any, scopes?: string[]): Promise<T> {
    const response = await this.makeRequest('POST', endpoint, data, scopes);
    return this.handleResponse<T>(response);
  }

  /**
   * Make authenticated PUT request
   */
  async put<T>(endpoint: string, data?: any, scopes?: string[]): Promise<T> {
    const response = await this.makeRequest('PUT', endpoint, data, scopes);
    return this.handleResponse<T>(response);
  }

  /**
   * Make authenticated DELETE request
   */
  async delete<T>(endpoint: string, scopes?: string[]): Promise<T> {
    const response = await this.makeRequest('DELETE', endpoint, undefined, scopes);
    return this.handleResponse<T>(response);
  }

  /**
   * Make authenticated PATCH request
   */
  async patch<T>(endpoint: string, data?: any, scopes?: string[]): Promise<T> {
    const response = await this.makeRequest('PATCH', endpoint, data, scopes);
    return this.handleResponse<T>(response);
  }

  /**
   * Make authenticated request with automatic token handling
   */
  private async makeRequest(
    method: string,
    endpoint: string,
    data?: any,
    scopes?: string[]
  ): Promise<Response> {
    const url = `${this.baseURL}${endpoint}`;
    const requestScopes = scopes || this.defaultScopes;

    const options: RequestInit = {
      method,
    };

    if (data) {
      options.body = JSON.stringify(data);
    }

    try {
      return await authService.authenticatedFetch(url, options, requestScopes);
    } catch (error) {
      console.error(`API ${method} request failed:`, error);
      throw error;
    }
  }

  /**
   * Handle API response
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await this.getErrorData(response);
      throw new ApiError(response.status, errorData.message || response.statusText, errorData);
    }

    // Handle empty responses (like 204 No Content)
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return {} as T;
    }

    try {
      return await response.json();
    } catch (error) {
      console.warn('Failed to parse JSON response:', error);
      return {} as T;
    }
  }

  /**
   * Extract error data from response
   */
  private async getErrorData(response: Response): Promise<any> {
    try {
      return await response.json();
    } catch {
      return { message: response.statusText };
    }
  }
}

/**
 * Custom API Error class
 */
export class ApiError extends Error {
  status: number;
  data?: any;

  constructor(
    status: number,
    message: string,
    data?: any
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

// Export configured API service instance
export const apiService = new ApiService(API_CONFIG);
export default apiService;